"use client"

import { motion } from "framer-motion"
import React from "react"
import { useTheme } from "../contexts/ThemeContext"

const Sparkline = () => (
  <svg width="100%" height="24" viewBox="0 0 80 24" preserveAspectRatio="none" style={{ display: "block", marginTop: 4 }}>
    <polyline
      points="0,20 13,16 28,18 42,9 54,12 67,6 80,2"
      fill="none" stroke="#22c55e" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
)

const DotMatrix = () => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 5 }}>
    {Array.from({ length: 20 }).map((_, i) => (
      <div key={i} style={{ width: 5, height: 5, borderRadius: 1.5, background: i < 14 ? "#1D3047" : "#e2e8f0" }} />
    ))}
  </div>
)

const MiniBar = () => {
  const bars = [35, 50, 42, 60, 48, 66, 75, 58]
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 24, marginTop: 5 }}>
      {bars.map((h, i) => (
        <div key={i} style={{
          flex: 1, height: h * 0.32, borderRadius: 2,
          background: i === bars.length - 1 ? "#E3B02E" : i === bars.length - 2 ? "#2D5F9E" : "#cbd5e1"
        }} />
      ))}
    </div>
  )
}

const features = [
  {
    color: "#2D5F9E", bg: "#EEF4FF",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>,
    title: "AI route optimization",
    desc: "Predictive scheduling that adapts to demand and traffic in real time.",
  },
  {
    color: "#E53E3E", bg: "#FFF5F5",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    title: "Live precision tracking",
    desc: "Sub-meter GPS plus telemetry for every bus, every stop, every minute.",
  },
  {
    color: "#D97706", bg: "#FFFBEB",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>,
    title: "Smart card boarding",
    desc: "Tap-to-board passes synced with student records and attendance.",
  },
  {
    color: "#16A34A", bg: "#F0FDF4",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22c6-3 10-7 10-11a10 10 0 1 0-20 0c0 4 4 8 10 11z"/></svg>,
    title: "Sustainable operations",
    desc: "Fuel analytics and route consolidation cut emissions by up to 30%.",
  },
  {
    color: "#7C3AED", bg: "#F5F3FF",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>,
    title: "Plug-in integration",
    desc: "Drops into your existing ERP, ID system and gate access in days, not months.",
  },
  {
    color: "#0891B2", bg: "#ECFEFF",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
    title: "24/7 intelligent alerts",
    desc: "Operator console with anomaly detection, SOS, and complaint triage.",
  },
]

const routes = [
  { dot: "#22c55e", name: "Main Campus Loop",      sub: "Bus 002 · Driver R. Hameed", pct: 98, bar: "#22c55e", badge: "On schedule",     bb: "#dcfce7", bc: "#16a34a" },
  { dot: "#E3B02E", name: "Library Shuttle",       sub: "Bus 014 · Driver A. Iqbal",  pct: 82, bar: "#E3B02E", badge: "+2 min delay",    bb: "#fef9c3", bc: "#a16207" },
  { dot: "#2D5F9E", name: "Sports Complex Express",sub: "Bus 007 · Driver M. Khan",   pct: 91, bar: "#2D5F9E", badge: "Arriving · 3 min", bb: "#dbeafe", bc: "#1d4ed8" },
  { dot: "#22c55e", name: "Hostel Connector",      sub: "Bus 021 · Driver S. Raza",   pct: 96, bar: "#22c55e", badge: "On schedule",     bb: "#dcfce7", bc: "#16a34a" },
]

export function AboutSection() {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  return (
    <section
      id="about"
      className="py-16 md:py-24 px-4 sm:px-6 lg:px-16 xl:px-24 relative overflow-hidden"
      style={{
        background: isDark
          ? "linear-gradient(160deg,#13161e 0%,#181c26 55%,#1b2230 100%)"
          : "linear-gradient(160deg,#F8FAFF 0%,#FFFFFF 55%,#FAFFF8 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 xl:gap-20 items-center">

          {/* ── LEFT ── */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            {/* section label */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="text-[10px] sm:text-[11px] font-bold tracking-widest uppercase" style={{ color: "#94a3b8" }}>
                02 — WHY SAFERSAVVY
              </span>
              <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1 text-[10px] sm:text-[11px] font-medium" style={{ color: "#64748b" }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="#E3B02E" stroke="#E3B02E" strokeWidth="1">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                Award-winning innovation · 2025
              </span>
            </div>

            {/* heading */}
            <h2 className="font-black leading-[1.05] tracking-tight mb-5" style={{ fontSize: "clamp(34px, 4.5vw, 52px)" }}>
              <span className="block" style={{ color: "#E3B02E" }}>Revolutionizing</span>
              <span className="block" style={{ color: "#1D3047" }}>Campus</span>
              <span className="block" style={{ color: "#1D3047" }}>Transport.</span>
            </h2>

            {/* description */}
            <p className="text-sm sm:text-[15px] leading-relaxed mb-8 max-w-lg" style={{ color: "#475569" }}>
              SaferSavvy pairs{" "}
              <strong style={{ color: "#1D3047" }}>cutting-edge AI</strong>{" "}
              with calm, focused design to give universities the safest, most efficient transport network on campus —{" "}
              <span style={{ textDecoration: "underline", textDecorationColor: "#E3B02E", textDecorationThickness: 2 }}>
                real-time tracking, smart routing, and accountability
              </span>{" "}
              in a single platform.
            </p>

            {/* 2-col feature grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-x-6 sm:gap-y-5 mb-8">
              {features.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.45 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3"
                >
                  <div className="flex-shrink-0 flex items-center justify-center rounded-[10px]"
                    style={{ width: 34, height: 34, background: f.bg, color: f.color }}>
                    {f.icon}
                  </div>
                  <div>
                    <p className="text-[12.5px] sm:text-[13px] font-bold mb-0.5" style={{ color: "#1D3047" }}>{f.title}</p>
                    <p className="text-[11px] sm:text-[11.5px] leading-snug" style={{ color: "#64748b" }}>{f.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-6">
              <button
                className="flex items-center gap-2 text-white rounded-[10px] font-semibold text-sm transition-opacity hover:opacity-90"
                style={{ background: "#1D3047", padding: "11px 20px" }}
              >
                Learn more about the platform
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
              <button
                className="flex items-center gap-2 font-semibold text-sm bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                style={{ color: "#1D3047" }}
              >
                Book a demo <span className="text-lg leading-none">—</span>
              </button>
            </div>
          </motion.div>

          {/* ── RIGHT: dashboard card ── */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative pb-6 sm:pb-7"
          >
            <div className="rounded-2xl overflow-hidden border border-slate-200"
              style={{ background: "white", boxShadow: "0 20px 60px rgba(29,48,71,0.10), 0 2px 8px rgba(0,0,0,0.04)" }}>

              {/* card header */}
              <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-slate-100">
                <span className="text-[9px] sm:text-[10px] font-bold tracking-widest uppercase" style={{ color: "#94a3b8" }}>
                  Platform Overview
                </span>
                <span className="flex items-center gap-1.5 text-[9px] sm:text-[10px] font-semibold" style={{ color: "#16a34a" }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                  All systems operational
                </span>
              </div>

              {/* 4 stats — 2 cols on mobile, 4 on sm+ */}
              <div className="grid grid-cols-2 sm:grid-cols-4 border-b border-slate-100">
                {[
                  {
                    label: "STUDENTS SERVED", val: "50K+", delta: "+12.4%", sub: "Across 4 campuses",
                    chart: <Sparkline />,
                  },
                  {
                    label: "SAFETY RATING", val: "95%", delta: "+2.1%", sub: "SLA compliance, YTD",
                    chart: (
                      <>
                        <div className="flex justify-between text-[8px] mt-1.5 mb-1" style={{ color: "#94a3b8" }}>
                          <span>0%</span><span>50%</span><span>100%</span>
                        </div>
                        <div className="rounded h-1.5 overflow-hidden" style={{ background: "#f1f5f9" }}>
                          <div className="h-full rounded" style={{ width: "95%", background: "#22c55e" }} />
                        </div>
                      </>
                    ),
                  },
                  {
                    label: "UNIVERSITIES", val: "100+", delta: "+8 new", sub: "In 14 countries",
                    chart: <DotMatrix />,
                  },
                  {
                    label: "EFFICIENCY BOOST", val: "30%", delta: "+5.3%", sub: "Avg. ops uplift",
                    chart: <MiniBar />,
                  },
                ].map((s, i) => (
                  <div key={i}
                    className="px-3 sm:px-4 py-3 sm:py-4"
                    style={{ borderRight: i < 3 ? "1px solid #f1f5f9" : "none" }}>
                    <p className="text-[8px] sm:text-[9px] font-bold tracking-widest uppercase mb-1.5" style={{ color: "#94a3b8" }}>{s.label}</p>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-lg sm:text-[22px] font-extrabold leading-none" style={{ color: "#1D3047" }}>{s.val}</span>
                      <span className="text-[9px] sm:text-[10px] font-bold" style={{ color: "#16a34a" }}>↑ {s.delta}</span>
                    </div>
                    {s.chart}
                    <p className="text-[9px] sm:text-[10px] mt-1" style={{ color: "#94a3b8" }}>{s.sub}</p>
                  </div>
                ))}
              </div>

              {/* Live Route Status */}
              <div className="px-4 sm:px-5 py-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold" style={{ color: "#1D3047" }}>Live Route Status</span>
                  <span className="text-[8px] sm:text-[9px] font-semibold tracking-widest uppercase hidden sm:block" style={{ color: "#94a3b8" }}>
                    Updated 2 sec ago · 14 routes
                  </span>
                </div>

                <div className="flex flex-col gap-2.5">
                  {routes.map((r, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + i * 0.08 }}
                      viewport={{ once: true }}
                      className="flex items-center gap-2 sm:gap-3"
                    >
                      <div className="flex-shrink-0 rounded-full" style={{ width: 7, height: 7, background: r.dot }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11.5px] sm:text-[12.5px] font-bold leading-tight truncate" style={{ color: "#1D3047" }}>{r.name}</p>
                        <p className="text-[9px] sm:text-[10px] truncate" style={{ color: "#94a3b8" }}>{r.sub}</p>
                      </div>
                      <div className="flex-shrink-0 hidden sm:block" style={{ width: 120 }}>
                        <div className="rounded h-1.5 overflow-hidden" style={{ background: "#f1f5f9" }}>
                          <div className="h-full rounded" style={{ width: `${r.pct}%`, background: r.bar }} />
                        </div>
                      </div>
                      <span className="text-[12px] sm:text-[13px] font-extrabold flex-shrink-0 w-8 text-right" style={{ color: "#1D3047" }}>{r.pct}%</span>
                      <span className="text-[9px] sm:text-[10px] font-bold rounded-full px-2 py-0.5 flex-shrink-0 hidden xs:block"
                        style={{ color: r.bc, background: r.bb, whiteSpace: "nowrap" }}>
                        {r.badge}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* footer */}
              <div className="border-t border-slate-100 px-4 sm:px-5 py-2.5 flex items-center justify-between">
                <div className="flex flex-wrap gap-3 sm:gap-4">
                  {["14 routes active", "62 buses on duty", "2,184 live riders"].map((t, i) => (
                    <span key={i} className="text-[10px] sm:text-[11px] font-medium" style={{ color: "#64748b" }}>{t}</span>
                  ))}
                </div>
                <div className="flex gap-0.5 items-end flex-shrink-0">
                  {Array.from({ length: 14 }).map((_, i) => (
                    <div key={i} style={{ width: 5, height: i < 12 ? 13 : 7, borderRadius: 2, background: i < 12 ? "#22c55e" : "#e2e8f0" }} />
                  ))}
                </div>
              </div>
            </div>

            {/* floating energy card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75 }}
              viewport={{ once: true }}
              className="absolute bottom-0 left-1/2 -translate-x-1/2 z-10"
            >
            <motion.div
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="flex items-center gap-2.5 bg-white rounded-[14px] border border-slate-200"
              style={{
                padding: "9px 16px",
                boxShadow: "0 8px 32px rgba(29,48,71,0.13)",
                whiteSpace: "nowrap",
              }}
            >
              <div className="flex items-center justify-center rounded-lg flex-shrink-0"
                style={{ width: 30, height: 30, background: "#dcfce7" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <div>
                <p className="text-[11.5px] font-bold leading-tight" style={{ color: "#1D3047" }}>Energy optimized</p>
                <p className="text-[9.5px]" style={{ color: "#64748b" }}>−30% fuel use</p>
              </div>
            </motion.div>
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  )
}

export default AboutSection
